// src/components/modals/ConstraintModal.js
import React, { useState, useEffect } from 'react';
import { useCrewMembersPG } from '../../hooks/useCrewMembersPG';

const DAYS_HE = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];

const DEFAULT_FORM = {
  reason: '',
  constraint_type: 'weekly',
  crew_member_id: '',
  days_of_week: [],
  start_time: '13:00',
  end_time: '14:00',
  start_date: '',
  end_date: '',
  all_day: false
};

/**
 * Modal for creating and editing schedule constraints.
 *
 * Props:
 *   isOpen        {boolean}
 *   onClose       {function}
 *   onSuccess     {function(constraintData, mode)} - called on create/update
 *   onDelete      {function(constraintId)}         - called on delete (edit mode only)
 *   constraint    {Object|null}                    - existing constraint data for edit mode
 *   mode          {'add'|'edit'}
 */
export function ConstraintModal({ isOpen, onClose, onSuccess, onDelete, constraint = null, mode = 'add', initialData = null }) {
  const { activeCrewMembers } = useCrewMembersPG();

  const [formData, setFormData] = useState({ ...DEFAULT_FORM });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      if (constraint && mode === 'edit') {
        setFormData({
          reason: constraint.reason || '',
          constraint_type: constraint.constraint_type || 'weekly',
          crew_member_id: constraint.crew_member_id || '',
          days_of_week: constraint.days_of_week || [],
          start_time: constraint.start_time || '13:00',
          end_time: constraint.end_time || '14:00',
          start_date: constraint.start_date || '',
          end_date: constraint.end_date || '',
          all_day: constraint.all_day || false
        });
      } else {
        setFormData(initialData ? { ...DEFAULT_FORM, ...initialData } : { ...DEFAULT_FORM });
      }
      setErrors({});
      setSubmitError(null);
    }
  }, [isOpen, constraint, mode, initialData]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const toggleDay = (dayIndex) => {
    setFormData(prev => {
      const current = prev.days_of_week || [];
      const next = current.includes(dayIndex)
        ? current.filter(d => d !== dayIndex)
        : [...current, dayIndex].sort((a, b) => a - b);
      return { ...prev, days_of_week: next };
    });
    if (errors.days_of_week) {
      setErrors(prev => ({ ...prev, days_of_week: '' }));
    }
  };

  const validate = () => {
    const errs = {};

    if (!formData.reason.trim()) {
      errs.reason = 'נדרשת סיבה';
    }

    if (formData.constraint_type === 'weekly') {
      if (!formData.days_of_week || formData.days_of_week.length === 0) {
        errs.days_of_week = 'יש לבחור לפחות יום אחד';
      }
      if (!formData.start_time || !formData.end_time) {
        errs.time = 'נדרשות שעת התחלה וסיום';
      } else if (formData.start_time >= formData.end_time) {
        errs.time = 'שעת הסיום חייבת להיות לאחר שעת ההתחלה';
      }
    }

    if (formData.constraint_type === 'date_range') {
      if (!formData.start_date || !formData.end_date) {
        errs.date_range = 'נדרשים תאריך התחלה וסיום';
      } else if (formData.start_date > formData.end_date) {
        errs.date_range = 'תאריך הסיום חייב להיות לאחר תאריך ההתחלה';
      }
      if (!formData.all_day) {
        if (!formData.start_time || !formData.end_time) {
          errs.time = 'נדרשות שעת התחלה וסיום';
        } else if (formData.start_time >= formData.end_time) {
          errs.time = 'שעת הסיום חייבת להיות לאחר שעת ההתחלה';
        }
      }
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const constraintData = {
        reason: formData.reason.trim(),
        constraint_type: formData.constraint_type,
        crew_member_id: formData.crew_member_id || null,
        ...(formData.constraint_type === 'weekly' ? {
          days_of_week: formData.days_of_week,
          start_time: formData.start_time,
          end_time: formData.end_time
        } : {
          start_date: formData.start_date,
          end_date: formData.end_date,
          all_day: formData.all_day,
          ...(!formData.all_day ? {
            start_time: formData.start_time,
            end_time: formData.end_time
          } : {})
        })
      };

      await onSuccess(constraintData, mode);
      onClose();
    } catch (error) {
      setSubmitError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!constraint || !onDelete) return;
    if (!window.confirm('האם למחוק את ההגבלה?')) return;

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await onDelete(constraint.id);
      onClose();
    } catch (error) {
      setSubmitError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const isEdit = mode === 'edit';
  const title = isEdit ? 'עריכת הגבלת זמן' : 'הגבלת זמן חדשה';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6" dir="rtl">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={isSubmitting}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-5">
            {/* Submit error */}
            {submitError && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-md p-3 text-sm">
                {submitError}
              </div>
            )}

            {/* Reason */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                סיבה <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.reason}
                onChange={e => handleChange('reason', e.target.value)}
                placeholder="למשל: הפסקת צהריים, חופשה..."
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm ${
                  errors.reason ? 'border-red-300' : 'border-gray-300'
                }`}
                disabled={isSubmitting}
              />
              {errors.reason && (
                <p className="text-red-500 text-xs mt-1">{errors.reason}</p>
              )}
            </div>

            {/* Crew member scope */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                חל על
              </label>
              <select
                value={formData.crew_member_id}
                onChange={e => handleChange('crew_member_id', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
                disabled={isSubmitting}
              >
                <option value="">כל הצוות</option>
                {activeCrewMembers.map(cm => (
                  <option key={cm.id} value={cm.id}>{cm.name}</option>
                ))}
              </select>
            </div>

            {/* Constraint type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                סוג הגבלה
              </label>
              <div className="flex gap-3">
                {[
                  { value: 'weekly', label: 'שבועי (חוזר)' },
                  { value: 'date_range', label: 'טווח תאריכים' }
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleChange('constraint_type', opt.value)}
                    className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                      formData.constraint_type === opt.value
                        ? 'bg-pink-50 border-pink-300 text-pink-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                    disabled={isSubmitting}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* ── Weekly fields ── */}
            {formData.constraint_type === 'weekly' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ימים בשבוע <span className="text-red-500">*</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {DAYS_HE.map((dayName, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => toggleDay(index)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                          formData.days_of_week.includes(index)
                            ? 'bg-pink-500 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        disabled={isSubmitting}
                      >
                        {dayName}
                      </button>
                    ))}
                  </div>
                  {errors.days_of_week && (
                    <p className="text-red-500 text-xs mt-1">{errors.days_of_week}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">שעת התחלה</label>
                    <input
                      type="time"
                      value={formData.start_time}
                      onChange={e => handleChange('start_time', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm ${
                        errors.time ? 'border-red-300' : 'border-gray-300'
                      }`}
                      disabled={isSubmitting}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">שעת סיום</label>
                    <input
                      type="time"
                      value={formData.end_time}
                      onChange={e => handleChange('end_time', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm ${
                        errors.time ? 'border-red-300' : 'border-gray-300'
                      }`}
                      disabled={isSubmitting}
                    />
                  </div>
                  {errors.time && (
                    <p className="col-span-2 text-red-500 text-xs -mt-1">{errors.time}</p>
                  )}
                </div>
              </>
            )}

            {/* ── Date range fields ── */}
            {formData.constraint_type === 'date_range' && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">תאריך התחלה</label>
                    <input
                      type="date"
                      value={formData.start_date}
                      onChange={e => handleChange('start_date', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm ${
                        errors.date_range ? 'border-red-300' : 'border-gray-300'
                      }`}
                      disabled={isSubmitting}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">תאריך סיום</label>
                    <input
                      type="date"
                      value={formData.end_date}
                      min={formData.start_date || undefined}
                      onChange={e => handleChange('end_date', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm ${
                        errors.date_range ? 'border-red-300' : 'border-gray-300'
                      }`}
                      disabled={isSubmitting}
                    />
                  </div>
                  {errors.date_range && (
                    <p className="col-span-2 text-red-500 text-xs -mt-1">{errors.date_range}</p>
                  )}
                </div>

                {/* All day toggle */}
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="all_day"
                    checked={formData.all_day}
                    onChange={e => handleChange('all_day', e.target.checked)}
                    className="w-4 h-4 text-pink-500 border-gray-300 rounded focus:ring-pink-500"
                    disabled={isSubmitting}
                  />
                  <label htmlFor="all_day" className="text-sm font-medium text-gray-700 cursor-pointer">
                    כל היום
                  </label>
                </div>

                {/* Time fields when not all-day */}
                {!formData.all_day && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">שעת התחלה (יומית)</label>
                      <input
                        type="time"
                        value={formData.start_time}
                        onChange={e => handleChange('start_time', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm ${
                          errors.time ? 'border-red-300' : 'border-gray-300'
                        }`}
                        disabled={isSubmitting}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">שעת סיום (יומית)</label>
                      <input
                        type="time"
                        value={formData.end_time}
                        onChange={e => handleChange('end_time', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm ${
                          errors.time ? 'border-red-300' : 'border-gray-300'
                        }`}
                        disabled={isSubmitting}
                      />
                    </div>
                    {errors.time && (
                      <p className="col-span-2 text-red-500 text-xs -mt-1">{errors.time}</p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer actions */}
          <div className="flex items-center justify-between mt-8 pt-4 border-t border-gray-100">
            {isEdit && onDelete ? (
              <button
                type="button"
                onClick={handleDelete}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
              >
                מחק הגבלה
              </button>
            ) : (
              <div />
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                ביטול
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-pink-500 hover:bg-pink-600 rounded-lg transition-colors disabled:opacity-50"
              >
                {isSubmitting
                  ? (isEdit ? 'מעדכן...' : 'שומר...')
                  : (isEdit ? 'עדכן הגבלה' : 'שמור הגבלה')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
